import React, { PureComponent } from "react";
import styled from "styled-components";
import { getRequest, patchRequest } from "../../api/ApiService";
import { dedupedGet } from "../../api/InFlightDedup";
import { changeLocation } from "../../api/Location";
import Auth from "../../routes/Auth";

// Results-Ready notification popup. Fires once at student login (via
// props from App.js) if there's an un-viewed round or peer-eval whose
// results are available.
//
// Priority (only one notification is shown per login):
//   1. Round popup — for the student's current round.
//      If current round has ENDED and rdN_viewed is false → show it.
//      If current round has NOT ended, check the PRIOR round only:
//        - Round 1 with no prior round → no popup.
//        - Round 2+ with prior rd(N-1)_viewed false → show prior round.
//      We deliberately don't cascade further back (e.g. Round 3 doesn't
//      check Round 1 if Round 2 was viewed).
//   2. Survey popup — only if no round popup was chosen this pass.
//      Initial peer eval first, then final peer eval.
//      Shows if the survey is past its `end` timestamp AND the matching
//      viewed flag is false.
//
// Action:
//   "View Results" click → PATCH /api/course_members/<id>/ with the
//   relevant viewed flag set to true, then navigate:
//     round   → /performance-feedback/results-are-in/round-team-score?round=N
//     survey  → /peer-review
//
// Data sources:
//   viewedResults, currentRound, rounds, courseMemberId  → props from App.js
//   surveys                                              → fetched on mount

const Backdrop = styled.div`
  position: fixed;
  top: 59px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 27, 59, 0.55);
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 520px;
  padding: 32px 36px;
  text-align: center;
  font-family: 'Mukta Vaani', sans-serif;
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 22px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 12px;
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.78);
  margin: 0 0 24px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const PrimaryButton = styled.button`
  background: #2DA548;
  border: 1px solid #2DA548;
  border-radius: 4px;
  padding: 10px 28px;
  font-family: 'Mukta Vaani', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;

  &:hover { background: #248339; border-color: #248339; }
  &:disabled { background: #a4d9b1; border-color: #a4d9b1; cursor: default; }
`;

class StudentResultsNotifications extends PureComponent {
  state = {
    surveys: [],
    surveysLoaded: false,
    // Set of surveyIds where the student's own peer-score row has
    // completion === true. Used to gate the peer-eval popup: if the
    // student hasn't completed peer eval for a survey, the peer-review
    // page would show its lock screen ("To unlock results, complete
    // peer evaluation"), so the popup should stay silent.
    completedSurveyIds: null,
    submitting: false,
    // True once "View Results" is clicked and the PATCH+navigate
    // completes — prevents the popup from re-appearing in the same
    // session while the local viewedResults prop still shows false
    // (until the next general-info refresh updates App state).
    dismissed: false,
  };

  componentDidMount() {
    this.fetchSurveys();
  }

  // Poll sessionStorage briefly for course_id. Same reason as
  // peer-review's fix — general-info may still be in flight when this
  // component mounts.
  waitForCourseId = () => new Promise(resolve => {
    let elapsed = 0;
    const check = () => {
      const id = Auth.getCourseId();
      if (id) return resolve(id);
      elapsed += 100;
      if (elapsed >= 5000) return resolve(0);
      setTimeout(check, 100);
    };
    check();
  });

  fetchSurveys = async () => {
    const courseId = await this.waitForCourseId();
    if (!courseId) {
      this.setState({ surveysLoaded: true });
      return;
    }
    const apiUrl = process.env.REACT_APP_API_URL;
    dedupedGet(`${apiUrl}v2/forms/surveys/?course=${courseId}`)
      .then(res => {
        const body = (res && res.body) || {};
        const list = Array.isArray(body) ? body : (body.results || []);
        // Defensive course filter — same pattern the onboarding uses,
        // since the endpoint occasionally returns rows for other
        // courses under the same account.
        const filtered = list.filter(
          s => Number(s.course) === Number(courseId)
        );
        this.setState({ surveys: filtered, surveysLoaded: true });
        // Fetch each closed survey's peer-score to see whether the
        // student themselves completed peer eval. If they didn't, the
        // peer-review page shows its "complete peer evaluation" lock
        // screen and the popup would send them nowhere useful.
        this.fetchCompletionForClosedSurveys(filtered);
      })
      .catch(() => {
        // Silent — no survey popup will fire without the survey list,
        // but the round popup path still works.
        this.setState({ surveysLoaded: true, completedSurveyIds: new Set() });
      });
  };

  fetchCompletionForClosedSurveys = (surveys) => {
    const closed = (surveys || []).filter(s => this.isSurveyClosed(s));
    if (closed.length === 0) {
      this.setState({ completedSurveyIds: new Set() });
      return;
    }
    const apiUrl = process.env.REACT_APP_API_URL;
    // Students have permission on /v2/forms/feedbacks/ (their own
    // feedback rows). The previous /v2/games/peer-score/<id>/ call
    // was instructor-only and 403'd here, which silently suppressed
    // the popup for every closed peer-eval. Mirror the Peer-Review
    // page's completion rule: a survey counts as completed when the
    // student has at least one feedback row for it AND none are
    // completed === false. One request total instead of N.
    dedupedGet(`${apiUrl}v2/forms/feedbacks/`)
      .then(res => {
        const body = (res && res.body) || {};
        const rows = Array.isArray(body) ? body : (body.results || []);
        const bySurvey = new Map();
        rows.forEach(r => {
          const sid = Number(r && r.survey);
          if (!bySurvey.has(sid)) bySurvey.set(sid, []);
          bySurvey.get(sid).push(r);
        });
        const completedSurveyIds = new Set(
          closed
            .filter(s => {
              const list = bySurvey.get(Number(s.id)) || [];
              if (list.length === 0) return false;
              return list.every(r => r.completed !== false);
            })
            .map(s => s.id)
        );
        this.setState({ completedSurveyIds });
      })
      .catch(() => {
        this.setState({ completedSurveyIds: new Set() });
      });
  };

  isSurveyClosed = (survey) => {
    if (!survey || !survey.end) return false;
    return new Date(survey.end).getTime() < Date.now();
  };

  isRoundEnded = (round) => {
    if (!round || !round.end_date) return false;
    return new Date(round.end_date).getTime() < Date.now();
  };

  // Returns { type: 'round', number: N } or
  //         { type: 'survey', which: 'initial'|'final' } or null.
  pickNotification = () => {
    const { viewedResults, currentRound, rounds } = this.props;
    const { surveys, surveysLoaded, completedSurveyIds } = this.state;
    if (!viewedResults) return null;

    // --- Round check (priority 1) ---
    // currentRound arrives as a NUMBER from App.js's getter, not the
    // round object — so we look up the object in `rounds` by number
    // to inspect its end_date / is_finished.
    // Gate: skip the popup for any round whose results are locked
    // (results_viewable === false). Clicking through would land on
    // the performance-feedback lock screen ("The instructor has
    // locked the results for this round.").
    const isRoundResultsLocked = (round) =>
      round && round.results_viewable === false;
    if (Array.isArray(rounds) && rounds.length > 0) {
      const n = Number(currentRound);
      const currentRoundObj = rounds.find(r => Number(r.number) === n);
      if (currentRoundObj) {
        if (this.isRoundEnded(currentRoundObj)) {
          const field = `rd${n}_viewed`;
          if (viewedResults[field] === false && !isRoundResultsLocked(currentRoundObj)) {
            return { type: 'round', number: n };
          }
        } else if (n > 1) {
          const priorRound = rounds.find(r => Number(r.number) === n - 1);
          if (priorRound) {
            const field = `rd${n - 1}_viewed`;
            if (viewedResults[field] === false && !isRoundResultsLocked(priorRound)) {
              return { type: 'round', number: n - 1 };
            }
          }
        }
      }
    }

    // --- Survey check (priority 2) ---
    // Final wins over initial. Once the final survey has closed, the
    // initial popup is stale news — even if the initial was never
    // reviewed, we only want to nudge the student toward the newest
    // available survey. Initial only fires when the final hasn't
    // closed yet (or doesn't exist for this course).
    // Gate: skip the popup for any survey the student hasn't
    // completed their own peer eval on — the peer-review page shows
    // its "To unlock results, complete peer evaluation" lock in
    // that case. Wait for completedSurveyIds to load before deciding.
    if (
      surveysLoaded
      && surveys.length > 0
      && completedSurveyIds !== null
    ) {
      const initial = surveys.find(s => s && s.stage === 'initial');
      const final = surveys.find(s => s && s.stage === 'final');
      const finalClosed = this.isSurveyClosed(final);
      if (
        finalClosed
        && viewedResults.final_survey_viewed === false
        && completedSurveyIds.has(final.id)
      ) {
        return { type: 'survey', which: 'final' };
      }
      if (
        !finalClosed
        && initial
        && this.isSurveyClosed(initial)
        && viewedResults.initial_survey_viewed === false
        && completedSurveyIds.has(initial.id)
      ) {
        return { type: 'survey', which: 'initial' };
      }
    }

    return null;
  };

  titleFor = (notif) => {
    if (notif.type === 'round') {
      return `Round ${notif.number} Results Ready to Review`;
    }
    return notif.which === 'initial'
      ? 'Initial Peer Evaluation Ready to Review'
      : 'Final Peer Evaluation Ready to Review';
  };

  descriptionFor = (notif) => {
    if (notif.type === 'round') {
      return "2 reports to check out. Start with the Performance Feedback to see how your team did and why any targets were missed. Then review the Team Reports to compare your moves against your competitors. Financial statements are included.";
    }
    // Same description for both initial and final peer evaluations.
    return "See what your teammates appreciate about you and where they'd like to see you do more or less. Your results also include a peer score that may factor into your overall score.";
  };

  redirectFor = (notif) => {
    if (notif.type === 'round') {
      return `/performance-feedback/results-are-in/round-team-score?round=${notif.number}`;
    }
    return '/peer-review';
  };

  fieldFor = (notif) => {
    if (notif.type === 'round') return `rd${notif.number}_viewed`;
    return notif.which === 'initial' ? 'initial_survey_viewed' : 'final_survey_viewed';
  };

  viewResults = () => {
    const notif = this.pickNotification();
    if (!notif) return;
    const { courseMemberId } = this.props;
    if (!courseMemberId || this.state.submitting) return;

    this.setState({ submitting: true });
    const apiUrl = process.env.REACT_APP_API_URL;
    const field = this.fieldFor(notif);
    const url = this.redirectFor(notif);

    patchRequest(`${apiUrl}course_members/${courseMemberId}/viewed/`)
      .send({ [field]: true })
      .then(() => {
        this.setState({ submitting: false, dismissed: true });
        changeLocation(url);
      })
      .catch(() => {
        // Navigate anyway — the student clicked View Results, we should
        // honor that. The viewed flag will re-sync on next login if the
        // PATCH silently succeeded server-side, or stay false and the
        // popup will re-appear (better than a broken affordance).
        this.setState({ submitting: false, dismissed: true });
        changeLocation(url);
      });
  };

  render() {
    // While RoundProcessingPopup is on screen it owns the flow — showing
    // this popup on top would double up on the "results ready" nudge
    // and cover the progress bar. Once round.is_finished flips true,
    // App.js's isRoundProcessing goes false → suppress lifts → this
    // popup fires with its own View Results button as normal.
    if (this.props.suppress) return null;
    if (this.state.dismissed) return null;
    const notif = this.pickNotification();
    if (!notif) return null;

    return (
      <Backdrop>
        <Card onClick={e => e.stopPropagation()}>
          <Title>{this.titleFor(notif)}</Title>
          <Description>{this.descriptionFor(notif)}</Description>
          <Actions>
            <PrimaryButton
              type="button"
              onClick={this.viewResults}
              disabled={this.state.submitting}
            >
              {this.state.submitting ? "Working…" : "View Results"}
            </PrimaryButton>
          </Actions>
        </Card>
      </Backdrop>
    );
  }
}

export default StudentResultsNotifications;



// WEBPACK FOOTER //
// ./src/components/student-notifications/StudentResultsNotifications.js